import type { FC, ReactNode } from "react";
import { t } from "@web-monorepo/i18n/macro";
import clsx from "clsx";
import { IconCheck, IconAlertCircleFilled, IconX } from "@web-monorepo/dds-icons";
import styles from "./DDSToast.module.css";
import { DDSButton } from "../../Buttons/DDSButton/DDSButton";
import { TranslatedString } from "@web-monorepo/translation-runtime";

export const Variants = ["neutral", "success", "error", "warning"] as const;
export type Variant = (typeof Variants)[keyof typeof Variants];

type IconType = FC<{ className: string }>;

type PropsWithIcon = {
  children?: never;
  variant?: "neutral";
  icon?: IconType;
  /** @deprecated Use children prop instead for more flexible content rendering */
  text: TranslatedString;
  /** @deprecated Use children prop with DDSButton component instead */
  onClickAction?: () => void;
  /** @deprecated Use children prop with DDSButton component instead */
  actionName?: TranslatedString;
};

type PropsWithoutIcon = {
  children?: never;
  variant: Variant;
  /** @deprecated Use children prop instead for more flexible content rendering */
  text: TranslatedString;
  icon?: never;
  /** @deprecated Use children prop with DDSButton component instead */
  onClickAction?: () => void;
  /** @deprecated Use children prop with DDSButton component instead */
  actionName?: TranslatedString;
};

type PropsWithChildren = {
  children: ReactNode;
  text?: never;
  variant: Variant;

  icon?: IconType | null;
  onClickAction?: never;
  actionName?: never;
};

const NoIcon: IconType = () => null;

const iconForVariant = (variant: Variant): IconType => {
  switch (variant) {
    case "success":
      return IconCheck;
    case "error":
    case "warning":
      return IconAlertCircleFilled;
  }

  return NoIcon;
};

type SharedProps = {
  /**
   * Shows a close (X) button that calls `closeToast`. When the toast is wrapped in another component,
   * react-toastify injects `closeToast` into the wrapper instead, and the X closes the toast through
   * the container's `closeOnClick`, which every app's `ToastContainer` sets.
   */
  dismissible?: boolean;
  /** Injected by react-toastify when the toast is passed to `toast()`. */
  closeToast?: () => void;
  /** Lets long text wrap onto several lines instead of keeping it on one. */
  wrap?: boolean;
};

export type Props = (PropsWithIcon | PropsWithoutIcon | PropsWithChildren) & SharedProps;

export const DDSToast: FC<Props> = ({
  children,
  variant = "neutral",
  icon: ProvidedIcon,
  text,
  onClickAction,
  actionName,
  dismissible,
  closeToast,
  wrap,
}) => {
  if (actionName && !onClickAction) {
    throw new Error("DDSToast: onClickAction is required when actionName is provided.");
  }

  const Icon = ProvidedIcon === null ? null : (ProvidedIcon ?? iconForVariant(variant));

  // Not an <aside>: stacked toasts would be duplicate complementary landmarks, and toast
  // containers already announce their content (e.g. react-toastify's role="alert").
  return (
    <div className={clsx(styles.toast, styles[variant as keyof typeof styles], wrap && styles.wrap)}>
      {Icon ? <Icon className={styles.icon} /> : null}
      {children ? children : <p className={styles.text}>{text}</p>}
      {actionName && (
        <DDSButton
          data-name="toast-action-button"
          variant="unstyled"
          size="small"
          className={styles.actionButton}
          onClick={onClickAction}
        >
          {actionName}
        </DDSButton>
      )}
      {dismissible && (
        <DDSButton
          data-name="toastCloseButton"
          variant="unstyled"
          className={styles.closeButton}
          onClick={closeToast}
          aria-label={t`Close`}
        >
          <IconX className={styles.icon} />
        </DDSButton>
      )}
    </div>
  );
};
